import { Link } from "react-router-dom"

import type {
  BatteryFinancingSelectionStatus,
  BatteryProposalDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalFinancing from "~/api/hooks/useBatteryProposalFinancing"
import { Drawer } from "~/components/ui-components/v2"

import { CatalogLoanProductPicker } from "../../ProposalSetup/financing/CatalogLoanProductPicker"
import { isBatteryProposalReadOnly } from "../battery-proposal-read-only"
import { batteryFinancingBundle } from "../financing-status/battery-financing-bundle"
import { LIGHTREACH_BATTERY_FINANCE_TYPE } from "../lightreach-battery"
import { Eyebrow } from "../primitives"
import { concertAmountBlockedPrompt } from "./concert-amount-copy"
import { ConcertOfferPicker } from "./ConcertOfferPicker"
import { btnLink, Pill, type Tone } from "./finance-product-chrome"
import { LightReachOfferPicker } from "./LightReachOfferPicker"
import { batterySetupStepPath } from "./setup-steps"
import { SungageBrightStartPicker } from "./SungageBrightStartPicker"

export {
  NOT_CALCULATED_LABEL,
  PAYMENT_OUT_OF_DATE_LABEL,
  PAYMENT_UNAVAILABLE_LABEL,
} from "../../ProposalSetup/financing/CatalogLoanProductPicker"
export {
  CALCULATE_PAYMENT_ERROR_PROMPT,
  PAYMENT_RETRY_PROMPT,
  PAYMENT_UNAVAILABLE_PROMPT,
  SAVE_AND_CALCULATE_LABEL,
  SAVED_AND_CALCULATED_LABEL,
  SAVED_WITHOUT_PAYMENT_LABEL,
} from "../financing-status/battery-financing-bundle"

export const FINANCE_PRODUCT_DIALOG_NAME = `Loan Product`
export const FINANCE_PARTNER_REQUIRED_PROMPT = `Select a Finance Partner before choosing a Loan Product.`
export const CASH_NO_LOAN_PRODUCT_PROMPT = `Cash requires no Loan Product.`
export const LOAN_PRODUCT_REQUIRED_PROMPT = `Select a Loan Product. None is selected by default.`
export const SELECTED_PRODUCT_UNAVAILABLE_PROMPT = `The saved Loan Product is no longer available. Select a different Loan Product.`
export const SELECTED_PRODUCT_OUTSIDE_LIMITS_PROMPT = `The saved Loan Product is outside the current Battery price limits. It remains selected for this proposal.`
export const FINANCING_LOAD_ERROR_PROMPT = `Unable to load financing`

/** Concert prices its own Battery offers, so it owns the whole picker. */
const CONCERT_FINANCE_TYPE = `concert_battery`
/** BrightStart quotes through Sungage preview/save, not Save & Calculate Payment. */
const SUNGAGE_FINANCE_TYPE = `sungage_battery`

/**
 * The banner only speaks up when something needs saying. A selected Loan
 * Product is already evident from the picker and the card's Saved chip, so
 * that status is excluded rather than given empty copy.
 */
const STATUS_COPY: Record<
  Exclude<BatteryFinancingSelectionStatus, `loan_product_selected`>,
  { headline: string; detail: string; tone: Tone }
> = {
  finance_partner_required: {
    headline: `Finance Partner required`,
    detail: FINANCE_PARTNER_REQUIRED_PROMPT,
    tone: `warn`,
  },
  no_loan_product_required: {
    headline: `Cash`,
    detail: CASH_NO_LOAN_PRODUCT_PROMPT,
    tone: `ok`,
  },
  loan_product_required: {
    headline: `Loan Product required`,
    detail: LOAN_PRODUCT_REQUIRED_PROMPT,
    tone: `warn`,
  },
  selected_product_unavailable: {
    headline: `Saved Loan Product unavailable`,
    detail: SELECTED_PRODUCT_UNAVAILABLE_PROMPT,
    tone: `danger`,
  },
  selected_product_outside_amount_limits: {
    headline: `Price outside Loan Product limits`,
    detail: SELECTED_PRODUCT_OUTSIDE_LIMITS_PROMPT,
    tone: `warn`,
  },
}

const FINANCE_PARTNER_LABELS: Record<string, string> = {
  cash: `Cash`,
  concert_battery: `Concert (Battery Only)`,
  credit_human: `Credit Human`,
  credit_human_battery: `Credit Human`,
  sungage_battery: `Sungage (Battery Only)`,
  lightreach_battery: `LightReach (Battery Only)`,
}

const financePartnerLabel = (financeType: string): string =>
  FINANCE_PARTNER_LABELS[financeType] ?? financeType

const equipmentLabel = (proposal: BatteryProposalDto): string | null => {
  const { equipment } = proposal
  if (!equipment) {
    return null
  }
  const name = [equipment.manufacturer?.trim(), equipment.name?.trim()]
    .filter(Boolean)
    .join(` `)
  return `${name} × ${equipment.quantity}`
}

export const FinanceProductStep = ({
  proposalId,
  onClose,
}: {
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  const proposalQuery = useBatteryProposal(proposalId)
  const financingQuery = useBatteryProposalFinancing(proposalId)

  const financing = financingQuery.data ?? null
  const proposal = proposalQuery.data ?? null
  const financeType =
    financing?.financePlan?.financeType ??
    proposal?.financePlan?.financeType ??
    null
  // A Concert price outside the configured range is not a fact about the saved
  // Loan Product, so the generic limits copy would misattribute it. The Concert
  // picker below owns that explanation, and names the bound and the price.
  const amountBlocked =
    concertAmountBlockedPrompt(financing?.concertAmount ?? null) != null
  const status =
    financing &&
    financing.selectionStatus !== `loan_product_selected` &&
    !amountBlocked
      ? STATUS_COPY[financing.selectionStatus]
      : null

  return (
    <Drawer
      open
      theme="dark"
      onClose={onClose}
      title={FINANCE_PRODUCT_DIALOG_NAME}
      description="Select a Loan Product for this Battery-Only Proposal."
      widthClass="sm:max-w-lg"
      contentClassName="flex min-h-0 flex-col gap-6"
      data-testid="finance-product-step"
    >
      {financingQuery.isLoading ? (
        <p className="text-[length:var(--step--1)] text-bp-text-muted">
          Loading financing
        </p>
      ) : null}

      {financingQuery.isError ? (
        <div className="flex flex-col gap-2">
          <p
            role="alert"
            className="text-[length:var(--step--2)] text-bp-danger"
          >
            {FINANCING_LOAD_ERROR_PROMPT}
          </p>
          <button
            type="button"
            onClick={() => {
              void financingQuery.refetch()
            }}
            className={btnLink}
          >
            Retry
          </button>
        </div>
      ) : null}

      {financing ? (
        <div className="flex flex-col gap-2">
          {/* Carried forward from Equipment, where the Finance Partner and
              the battery are chosen. Labeled so it reads as that step's
              answers rather than as metadata about this one. */}
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <Eyebrow>From Equipment</Eyebrow>
            {financing.financePlan ? (
              <Link
                to={batterySetupStepPath(proposalId, `equipment`)}
                className="text-[length:var(--step--2)] font-medium text-bp-text underline underline-offset-2 hover:text-bp-text-soft"
              >
                Change
              </Link>
            ) : null}
          </div>
          <p className="flex flex-wrap items-center gap-x-1.5 text-[length:var(--step--2)] text-bp-text-muted">
            <span className="font-semibold text-bp-text-soft">
              {financing.financePlan
                ? financePartnerLabel(financing.financePlan.financeType)
                : `No Finance Partner`}
            </span>
            {proposal && equipmentLabel(proposal) ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{equipmentLabel(proposal)}</span>
              </>
            ) : null}
          </p>
          {status ? (
            <>
              <div>
                <Pill tone={status.tone}>{status.headline}</Pill>
              </div>
              <p
                role="status"
                className="text-[length:var(--step--2)] text-bp-text-muted"
              >
                {status.detail}
              </p>
            </>
          ) : null}
        </div>
      ) : null}

      {financing?.selectionStatus === `finance_partner_required` ? (
        <Link
          to={batterySetupStepPath(proposalId, `equipment`)}
          className="self-start font-medium text-bp-text underline underline-offset-2 hover:text-bp-text-soft"
        >
          Open Finance Partner &amp; Equipment
        </Link>
      ) : null}

      {financeType === CONCERT_FINANCE_TYPE ? (
        <ConcertOfferPicker
          proposalId={proposalId}
          quote={financing?.quote ?? null}
          onClose={onClose}
        />
      ) : financeType === LIGHTREACH_BATTERY_FINANCE_TYPE ? (
        // LightReach prices its own Energy Backup offers, so it owns the picker.
        <LightReachOfferPicker
          proposalId={proposalId}
          quote={financing?.quote ?? null}
          financing={financing ?? null}
          onClose={onClose}
        />
      ) : financeType === SUNGAGE_FINANCE_TYPE && financing ? (
        <SungageBrightStartPicker
          proposalId={proposalId}
          financing={financing}
          canEdit={
            (proposal == null || !isBatteryProposalReadOnly(proposal)) &&
            financing.currentApplication?.editLocked !== true &&
            financing.financingLocked !== true
          }
          onClose={onClose}
        />
      ) : financeType === SUNGAGE_FINANCE_TYPE ||
        (financeType == null &&
          (financingQuery.isLoading || proposalQuery.isLoading)) ? null : (
        <CatalogLoanProductPicker
          bundle={batteryFinancingBundle}
          proposalId={proposalId}
          financing={financing}
          onClose={onClose}
        />
      )}
    </Drawer>
  )
}
